import { useQuery } from '@apollo/client'
import { ProductVariant, Query } from '@danone-global/ct/interfaces'
import { FieldsStepper } from '@danone-global/internal/react/components/fields'
import {
  AddToCartIcon,
  EnvelopeIcon,
} from '@danone-global/internal/react/components/icons'
import {
  Button,
  ButtonVariants,
  Grid,
  Notification,
  Typography,
} from '@danone-global/internal/react/components/ui'

import {
  analytics,
  ANALYTICS_CATEGORY_LIMIT,
  isLoggedIn,
  limitArrayCharacters,
  PACKAGE_PRODUCT_KEY,
  Theme,
  useCart,
  useCore,
} from '@danone-global/internal/react/core'
import {
  productStructuredData,
  useAddProductToCart,
  useProductUrl,
  useUpdateQuantityFromCart,
} from '@danone-global/internal/react/modules/product'
import {
  getChannelAvailability,
  useChannelId,
} from '@danone-global/internal/react/modules/stock'
import { makeStyles } from '@material-ui/styles'
import * as Sentry from '@sentry/react'
import clsx from 'clsx'
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import TextField from '@mui/material/TextField';

import { GET_PRODUCT_QUERY } from './add-to-cart-button.graphql'
import messages from './add-to-cart-button.messages'

const NotifyMeModal = React.lazy(() => import('./notify-me-modal'))

export interface Props {
  sku: string | number
  channel?: string
  stockChannel?: string
  label?: string
  labelNotAvailable?: string
  labelOutOfStock?: string
  labelPackageProduct?: string
  amount?: string
  stepper?: boolean
  variant?: ButtonVariants
  analytics?: boolean
  subscriptionId?: string
  structuredData?: boolean | string
  narrow?: boolean
}

export const WidgetAddToCartButton: React.FC<Props> = ({
  sku: providedSku,
  amount = '1',
  stepper = false,
  channel: providedPriceChannelId = null,
  stockChannel: providedStockChannelId = null,
  label: providedLabel,
  labelNotAvailable: providedLabelNotAvailable,
  labelOutOfStock: providedLabelOutOfStock,
  labelPackageProduct: providedLabelPackageProduct,
  structuredData: provideStructuredData = true,
  variant = 'brand',
  analytics: withAnalytics = true,
  subscriptionId,
  narrow = false,
}) => {
  const intl = useIntl()
  const [priceChannelId, setPriceChannelId] = useState<string>(
    useChannelId(providedPriceChannelId),
  )
  const [stockChannelId, setStockChannelId] = useState<string>(
    useChannelId(providedStockChannelId),
  )
  const [isDefaultChannel, setIsDefaultChannel] = useState(true)
  const [patientName, setPatientName] = useState<string>('');

  const {
    features,
    config: { siteName },
    localeConfig: { currency, country, locale },
  } = useCore()
  const classes = useStyles()
  const [state, setState] = useState({
    error: '',
    loading: true,
    isOnStock: false,
    channel: false,
  })
  const [cart] = useCart()

  const hkSites = ['hk-am', 'hk-cg', 'hk-ganmai', 'hk-nutrilon']
  const siteAvailable = hkSites.includes(siteName)

  const [quantity, setQuantity] = useState(parseInt(amount, 10) || 1)
  // Sites that excludes the outOfStock scenarios
  const sites = ['uk-medical-uk', 'uk-am', 'uk-medical-ie', 'uk-cg']
  const siteNotAvailable = sites.includes(siteName)

  /** Check if user is not logged in then return null */
  if (!isLoggedIn() && features.hideAddToCartForGuestUser) {
    return null
  }

  // Force SKU to string
  const sku = useMemo(() => `${providedSku}`, [providedSku])
  const addProductToCart = useAddProductToCart({
    withAnalytics,
    cart,
    subscriptionId: features.enableChargebeeSubscriptions
      ? subscriptionId
      : undefined,
  })
  const updateLineItem = useUpdateQuantityFromCart({
    withAnalytics,
    subscriptionId: features.enableChargebeeSubscriptions
      ? subscriptionId
      : undefined,
  })
  const { data, loading: productLoading } = useQuery<Query>(GET_PRODUCT_QUERY, {
    variables: { sku, currency, country, locale },
  })

  const isPackageProduct =
    data?.product?.productType?.key === PACKAGE_PRODUCT_KEY

  const masterData = data?.product?.masterData?.current
  const isPublished = data?.product?.masterData?.published

  const isPrice =
    data?.product?.masterData?.current.variant?.price?.discounted?.value ||
    data?.product?.masterData?.current.variant?.price?.value

  const notifyMeValue: 'never' | 'out_of_stock' | 'always' =
    masterData?.variant?.attributesRaw?.find((i) => i.name === 'customerAlerts')
      ?.value?.key

  const notifyMeEnabled =
    features.enableNotifyMe &&
    (notifyMeValue === 'always' ||
      (state.channel && !state.isOnStock && notifyMeValue === 'out_of_stock'))

  const [modalOpen, setModalOpen] = useState(false)
  const [isAvailable, setIsAvailable] = useState(true)
  const sendAnalyticsRef = React.useRef(false)

  const { config } = useCore()
  const url = useProductUrl(masterData?.variant?.attributesRaw ?? [])

  const richSnippet =
    provideStructuredData === true
      ? productStructuredData(masterData, config.storeName, sku, locale, url)
      : undefined

  const label = useMemo(() => {
    let message = providedLabel || intl.formatMessage(messages.label)

    // Package products have a default warning label
    if (productLoading) {
      return message
    } else if (isPackageProduct) {
      message =
        providedLabelPackageProduct ||
        intl.formatMessage(messages.packageProduct)
    } else if (!state.channel) {
      message =
        providedLabelNotAvailable || intl.formatMessage(messages.notAvailable)
    } else if (notifyMeEnabled) {
      message = intl.formatMessage(messages.notifyMe)
    } else if (!state.isOnStock) {
      message =
        providedLabelOutOfStock || siteAvailable ? intl.formatMessage(messages.callForEnquiries) : intl.formatMessage(messages.outOfStock)
    }

    return message
  }, [
    state,
    productLoading,
    isPackageProduct,
    providedLabel,
    providedLabelNotAvailable,
    providedLabelPackageProduct,
    providedLabelOutOfStock,
    notifyMeEnabled,
    intl,
  ])

  const handleOnClick = useCallback(() => {

    if (notifyMeEnabled) {
      setModalOpen(true)
      return
    }

    const results =
      data?.product?.masterData?.current?.variant?.availability?.channels
        ?.results
    const lineItem = cart?.lineItems

    const dataSku = data?.product?.masterData?.current?.variant.sku
    const channel = getChannelAvailability(results, stockChannelId)

    const lineItemQuantity =
      lineItem?.find((i) => i?.variant.sku === dataSku)?.quantity || 0

    if (
      results.length > 0 &&
      lineItemQuantity + quantity > channel.availableQuantity &&
      !siteNotAvailable
    ) {
      setIsAvailable(false)
      return
    }

    setState((oldState) => ({
      ...oldState,
      error: '',
      loading: true,
    }))

    let patientNamesParse;
    // Determine if patientNamesString is a JSON array or a single string
    let parsed = [];
     /* istanbul ignore next */
    try {
      parsed = JSON.parse(patientName);
      if (Array.isArray(parsed)) {
        patientNamesParse = parsed;
      } else {
        patientNamesParse = [parsed]; // Single string, convert to array
      }
    } catch (e) {
      patientNamesParse = [patientName]; // Default to an empty array if parsing fails
    }

    /* istanbul ignore next */
    // Filter lineItems
    const lineItems = [...(cart?.lineItems || [])].filter((lineItem) => {
      // Check if the lineItem matches the target SKU
      const matchesSku = lineItem.variant.sku === data?.product.masterData.current.variant.sku;

      // Check if lineItem.custom.customFieldsRaw contains values included in patientNamesParse
      const matchesCustomFields = lineItem.custom?.customFieldsRaw?.some((item) => {

        // Parse item.value if it's a JSON string, otherwise handle it as a single string
        let values;
        try {
          values = JSON.parse(item.value);
          if (!Array.isArray(values)) {
            values = [values];
          }
        } catch {
          // If JSON parsing fails, assume item.value is a single string
          values = [item.value];
        }

        // Check if any value is included in patientNamesParse
        return values.some(value => patientNamesParse.includes(value));
      });

      // Return true only if both conditions are met
      return matchesSku && matchesCustomFields;
    });
    /* istanbul ignore next */
    if (lineItems.length > 0) {
      let filteredLineItems = lineItems[0];

      const variant = data?.product.masterData.current.variant;
      const name = data?.product.masterData.current.name
      const categories = data?.product.masterData.current.categories
      return updateLineItem(quantity, filteredLineItems, [
        {
          variant,
          priceChannelId,
          stockChannelId,
          quantity,
          name,
          categories,
          isDefaultChannel,
          patientName
        }
      ])
      .then(() => {
        setState((oldState) => ({
          ...oldState,
          loading: false,
        }))
      })
      .catch((error) => {
        let message
        const codeErr =
          error?.graphQLErrors?.[0]?.code ||
          error?.graphQLErrors?.[0].extensions.code
        if (
          error.message?.toLowerCase().includes('usage of unpublished products')
        ) {
          message = intl.formatMessage(
            messages.addToCartButtonUnpublishedErrorMessage,
          )
        } else if (codeErr === 'InvalidOperation') {
          message =
            error?.graphQLErrors?.[0]?.localizedMessage?.[locale] ||
            error?.graphQLErrors?.[0]?.extensions.localizedMessage?.[locale] ||
            error.message
        } else {
          message = intl.formatMessage(
            messages.addToCartButtonCustomErrorMessage,
          )
        }

        setState((oldState) => ({
          ...oldState,
          loading: false,
          error: message,
        }))
      })
    }
    return addProductToCart(
      data?.product.masterData.current.variant,
      priceChannelId,
      stockChannelId,
      quantity,
      data?.product.masterData.current.name,
      data?.product.masterData.current.categories,
      isDefaultChannel,
      patientName

    )
      .then(() => {
        setState((oldState) => ({
          ...oldState,
          loading: false,
        }))
      })
      .catch((error) => {
        let message
        const codeErr =
          error?.graphQLErrors?.[0]?.code ||
          error?.graphQLErrors?.[0].extensions.code
        if (
          error.message?.toLowerCase().includes('usage of unpublished products')
        ) {
          message = intl.formatMessage(
            messages.addToCartButtonUnpublishedErrorMessage,
          )
        } else if (codeErr === 'InvalidOperation') {
          message =
            error?.graphQLErrors?.[0]?.localizedMessage?.[locale] ||
            error?.graphQLErrors?.[0]?.extensions.localizedMessage?.[locale] ||
            error.message
        } else {
          message = intl.formatMessage(
            messages.addToCartButtonCustomErrorMessage,
          )
        }

        setState((oldState) => ({
          ...oldState,
          loading: false,
          error: message,
        }))
      })
  }, [
    addProductToCart,
    data,
    quantity,
    priceChannelId,
    stockChannelId,
    locale,
    notifyMeEnabled,
  ])

  useEffect(() => {
    if (data?.product) {
      const results =
        data.product.masterData.current.variant.availability.channels.results

      if (results.length > 0) {
        const channel = getChannelAvailability(results, stockChannelId)
        if (withAnalytics && data.product.masterData.published && !sendAnalyticsRef.current) {
          sendAnalyticsRef.current = true

          analytics.push({
            event: "view_item",
            action: 'VIEW_ITEM',
            ecommerce: {
              currency: data?.product?.masterData?.current?.variant?.price?.value?.currencyCode,
              value: (data?.product.masterData.current.variant?.price?.value
                ?.centAmount || 0) / 100,
              items: [
                {
                  item_id: sku,
                  item_name: data?.product?.masterData?.current?.name,
                  item_category: limitArrayCharacters(
                    data?.product?.masterData?.current?.categories
                      .map(({ name }) => name)
                      .join(',') || config?.analytics?.defaultCategory,
                    ANALYTICS_CATEGORY_LIMIT,
                  ),
                  price:
                    (data?.product.masterData.current.variant?.price?.value
                      ?.centAmount || 0) / 100,
                  discount: (data?.product.masterData.current.variant?.price
                    ?.discounted?.value?.centAmount / 100 || 0),
                  affiliation: config.storeName,
                  item_variant: data?.product?.masterData?.current?.variant?.key
                },
              ],
            }
          })
        }
        setState({
          loading: false,
          channel: Boolean(channel.id),
          isOnStock: channel.isOnStock,
          error: '',
        })
      } else {
        setState({
          loading: false,
          isOnStock: false,
          channel: false,
          error: '',
        })
      }
    }
  }, [data, stockChannelId, priceChannelId])

  useEffect(() => {
    if (productLoading === false) {
      setState((oldState) => ({
        ...oldState,
        loading: false,
      }))
    }
  }, [productLoading])

  useEffect(() => {
    setQuantity(parseInt(amount, 10) || 1)
  }, [amount])

  const updateChannelIdSelected = useCallback(
    (event: Event & { detail: { channelId: string } }) => {
      const newChannelId = event.detail.channelId
        ? event.detail.channelId
        : config.defaultChannelId
      const getChannelDetail = event.detail
      setPriceChannelId(newChannelId)
      getChannelDetail.channelId === '' ? setIsDefaultChannel(true) : setIsDefaultChannel(false)
    },
    [],
  )
  React.useEffect(() => {
    if (window) {
      window.addEventListener(
        `dtc-send-price-channel-${sku}`,
        updateChannelIdSelected,
      )
      return () => {
        window.removeEventListener(
          `dtc-send-price-channel-${sku}`,
          updateChannelIdSelected,
        )
      }
    }
  }, [sku, updateChannelIdSelected])

  if (data?.product && !isPublished) {
    return null
  }
  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    // Extract the value from the event based on the element type
    const value = event.target.value;
    setPatientName(value);
  };

  return (
    <Grid
      className={clsx('dtc-widget-add-to-cart-button__root', classes.root)}
      direction="column"
      spacing={8}
      container
    >
      {richSnippet ? (
        <script type="application/ld+json">
          {JSON.stringify(richSnippet)}
        </script>
      ) : undefined}

      {stepper && (
        <Grid item>
          <FieldsStepper
            data-testid={`add-to-cart-button-${sku}`}
            onDecrease={() => setQuantity((q) => Math.max(1, q - 1))}
            onIncrease={() => setQuantity((q) => q + 1)}
            value={quantity}
          />
        </Grid>
      )}

      {notifyMeEnabled ? (
        <Typography className={classes.notifyMeOutOfStock} type="body">
          {providedLabelOutOfStock || intl.formatMessage(messages.outOfStock)}
        </Typography>
      ) : undefined}

      {notifyMeEnabled && modalOpen ? (
        <Suspense fallback={null}>
          <NotifyMeModal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            sku={sku}
          />
        </Suspense>
      ) : undefined}
      <Grid item>
        <Button
          className={clsx(
            'dtc-widget-add-to-cart-button__button',
            classes.button,
            narrow && classes.narrow,
          )}
          data-state={state.loading ? 'loading' : 'loaded'}
          data-testid={`btn.add-to-cart-button-${sku}`}
          disabled={
            productLoading ||
            isPackageProduct ||
            (!notifyMeEnabled && (!state.isOnStock || !state.channel)) ||
            !isPrice
          }
          loading={productLoading || state.loading}
          onClick={handleOnClick}
          variant={notifyMeEnabled ? 'default' : variant}
        >
          {!narrow && label}

          {narrow && !notifyMeEnabled ? (
            <AddToCartIcon height={24} width={24} />
          ) : undefined}

          {notifyMeEnabled && !siteAvailable ? (
            <EnvelopeIcon className={classes.icon} />
          ) : undefined}
        </Button>
        {state.error ? (
          <div
            data-testid="notificationWidget"
            onClick={() => setState((s) => ({ ...s, error: '' }))}
          >
            <Notification
              className={classes.notification}
              indicator="top-left"
              message={state.error}
              error
            />
          </div>
        ) : undefined}

        {!isAvailable ? (
          <div
            data-testid="notificationWidget"
            onClick={() => setIsAvailable(true)}
          >
            <Notification
              className={classes.notification}
              indicator="top-left"
              message={intl.formatMessage(
                messages.addToCartButtonQuantityExceededErrorMessage,
              )}
              error
            />
          </div>
        ) : undefined}
        {features.siteBE ?
          (<>
            <div style={{ marginTop: '10px' }}>
              <TextField
                className={classes.patientinput}
                size='small'
                data-testid="patient-name-input"
                id="patient-name"
                type="text"
                autoComplete="off"
                value={patientName}
                onChange={handleChange}
                placeholder={intl.formatMessage({
                  id: 'line-item.add-patientname-placeholder',
                  defaultMessage: 'fill in patient name',
                })}
                inputProps={{
                  maxLength: 30
                }}
              ></TextField>
            </div>
          </>
          ) : undefined}
      </Grid>
    </Grid >
  )
}

export default Sentry.withProfiler(WidgetAddToCartButton, {
  name: 'WidgetAddToCartButton',
})

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      position: 'relative',
      marginBottom: theme.spacing(),

      '& label': {
        display: 'block',
        marginBottom: theme.spacing(),
      },
    },
    notification: {
      position: 'absolute',
      zIndex: 100,
    },
    notifyMeOutOfStock: {
      color: theme.palette.warning.main,
      fontWeight: 500,
      margin: 0,
    },
    button: {
      '& span': {
        alignItems: 'center',
        display: 'inline-flex',
      },
    },
    icon: {
      marginLeft: theme.spacing(1.5),
      width: 20,
      height: 20,
    },
    narrow: {
      paddingLeft: 16,
      paddingRight: 16,

      '& $icon': {
        marginLeft: 0,
      },
    },
    patientinput: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      minWidth: 'auto',
      border: '1px solid rgba(151, 151, 152, 0.25)',
      padding: '8px',
      fontSize: '16px',
      fontWeight: theme.typography.fontWeightLight,
      fontFamily: theme.typography.fontFamily,
      outline: 'none',
      transition: 'all 200ms ease-out',
      borderRadius: 8,
      '&:disabled': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
      },
    },

  }),
  { name: 'AddToCart' },
)

